import { computed, inject, Injector, Type } from '@angular/core';

import { tapResponse } from '@ngrx/operators';
import {
  patchState,
  signalStore,
  withComputed,
  withFeature,
  withMethods,
  withProps,
  withState,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { ShallowCamelCasePipe } from '@shared/pipes/shallow-camel-case.pipe';
import { WcgTableUtilsService } from '@shared/services/wcg-table-utils.service';
import {
  BuildLinkFromValue,
  GridColumnCasePipe,
} from '@wcg/frontend-framework';
import { map, pipe, switchMap, tap } from 'rxjs';
import {
  ORGANIZATION_FACILITIES_GRID_STATE,
  ORGANIZATION_URL,
  organizationFacilitiesInitialState,
  SITE_URL,
} from '../../constants';

import { FacilitiesListService } from '../../services';

import { withOrganizationFacilitiesEntities } from '../entities';
import {
  FacilitiesGridTypes,
  LoadGridPayload,
  OrganizationFacilities,
  OrganizationFacilitiesSignalState,
} from '../../types';
import { withGridConfigFeatures } from '../features';
import { transformGridDateFormat } from '@data-access/shared/utils';

export const ORGANIZATION_FACILITIES_SIGNAL_STORE = signalStore(
  withState(() => inject(ORGANIZATION_FACILITIES_GRID_STATE)),
  withProps(
    (store, _facilitiesListService = inject(FacilitiesListService)) => ({
      _gridCasePipe: new GridColumnCasePipe(),
      _facilitiesListService,
      _shallowCamelCasePipe: new ShallowCamelCasePipe(),
      _buildLinkFromValue: new BuildLinkFromValue(),
      _wcgTableUtilService: inject(WcgTableUtilsService),
      _injector: inject(Injector),
      store,
    }),
  ),
  withFeature(() => withGridConfigFeatures()),
  withFeature(() => withOrganizationFacilitiesEntities()),
  withComputed(
    ({
      error,
      _facilitiesEntities,
      total,
      isLoading,
      currentUrl,
      gridType,
      store,
      _injector,
    }) => ({
      $currentUrl: computed(() => currentUrl()),
      $error: computed(() => error()),

      $facilities: computed(() => {
        const _facilities = _facilitiesEntities();
        if (!_facilities) {
          return [] as OrganizationFacilities[];
        }

        return transformGridDateFormat<OrganizationFacilities>(
          _injector,
          _facilities,
          store.columnsCopy(),
        );
      }),
      $gridType: computed(() => gridType()),
      $isLoading: computed(() => isLoading()),
      $totalCount: computed(() => total()),
    }),
  ),
  withMethods(
    ({
      _shallowCamelCasePipe,
      $currentUrl,
      _wcgTableUtilService,
      _buildLinkFromValue,
      _facilitiesListService,
      ...store
    }) => {
      const _loadFacilities = rxMethod<LoadGridPayload>(
        pipe(
          tap(() => patchState(store, { isLoading: true })),
          switchMap(({ params, updateColumns }) =>
            _facilitiesListService.getFacilities($currentUrl(), params).pipe(
              map((response) => ({
                ...response,
                config: _shallowCamelCasePipe.transform(response.config),
              })),
              tapResponse({
                error: (error: Error) =>
                  patchState(store, { error: error, isLoading: false }),
                finalize: () => patchState(store, { isLoading: false }),
                next: (response) => {
                  patchState(store, {
                    currentStateDestroyed: false,
                    total: response.total,
                  });
                  store.loadFacilities(response.data);
                  store.loadConfigurations(
                    response.config,
                    updateColumns,
                    response.data?.[0],
                    _buildLinkFromValue,
                  );
                  store.setGridViews(response.views);
                },
              }),
            ),
          ),
        ),
      );

      const _getGridMetaData = (
        type: string,
        entityId?: string,
      ): { currentUrl: string } => {
        switch (type) {
          case 'organization':
            return {
              currentUrl: `${ORGANIZATION_URL}${entityId}/facilities/`,
            };
          case 'site':
            return {
              currentUrl: `${SITE_URL}${entityId}/location/`,
            };
          default:
            throw new Error('Invalid type for loading facilities grid');
        }
      };

      const _setGridMetaData = (
        type: FacilitiesGridTypes,
        organizationId?: string,
      ) => {
        const { currentUrl } = _getGridMetaData(type, organizationId);
        patchState(store, { currentUrl });
      };

      return {
        loadFacilitiesGrid: (payload: LoadGridPayload) => {
          return _loadFacilities(payload);
        },
        resetGridSettings: (payload: LoadGridPayload) => {
          _loadFacilities(payload);
          patchState(store, { searchString: '' });
          store.setDefaultGridView();
        },
        resetState: () =>
          patchState(store, {
            ...organizationFacilitiesInitialState,
            currentStateDestroyed: true,
          }),
        setByURL: (type: FacilitiesGridTypes, entityId?: string) =>
          _setGridMetaData(type, entityId),
      };
    },
  ),
) satisfies Type<OrganizationFacilitiesSignalState>;
